import { createTheme } from '@mui/material/styles';

// Professional color palette - ZonNet brand identity
const candidateTheme = createTheme({
  palette: {
    primary: {
      main: '#29204C', // ZonNet brand primary color
      light: '#4a3a6b',
      dark: '#1a1530',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f5f5f5', // Light gray for cards and backgrounds
      light: '#ffffff',
      dark: '#e0e0e0',
      contrastText: '#333333',
    },
    accent: {
      main: '#68B977', // ZonNet secondary green color
      light: '#85c88e',
      dark: '#4a8455',
    },
    background: {
      default: '#fafafa', // Very light gray background
      paper: '#ffffff', // White cards/containers
    },
    text: {
      primary: '#333333', // Dark gray for main text
      secondary: '#666666', // Medium gray for secondary text
      disabled: '#999999', // Light gray for disabled text
    },
    divider: '#e0e0e0',
    error: {
      main: '#f44336',
      light: '#ef5350',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    success: {
      main: '#68B977', // ZonNet green for success states
      light: '#85c88e',
      dark: '#4a8455',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
      color: '#333333',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#333333',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#333333',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#333333',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#333333',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#333333',
    },
    body1: {
      fontSize: '1.1rem',
      fontWeight: 500,
      lineHeight: 1.6,
      color: '#333333',
    },
    body2: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#666666',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none', // Remove uppercase transformation
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      color: '#999999',
    },
  },
  shape: {
    borderRadius: 8, // Consistent border radius
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
    '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)',
    '0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)',
    // ... other shadow levels
  ],
  components: {
    // Button component styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #342854 0%, #29204C 100%)',
            boxShadow: '0px 4px 8px rgba(41, 32, 76, 0.3)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: '#29204C',
          color: '#29204C',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(41, 32, 76, 0.04)',
          },
        },
      },
    },
    // Card component styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f0f0f0',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    // TextField component styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#e0e0e0',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: '#29204C',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#29204C',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666666',
            fontSize: '1rem',
            fontWeight: 500,
            '&.Mui-focused': {
              color: '#29204C',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '1.1rem',
            fontWeight: 500,
            color: '#333333',
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.875rem',
            fontWeight: 400,
          },
        },
      },
    },
    // Paper component styling
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #f0f0f0',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Chip component styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 28,
        },
        filled: {
          backgroundColor: '#f5f5f5',
          color: '#333333',
          '&:hover': {
            backgroundColor: '#e0e0e0',
          },
        },
      },
    },
    // Select component styling
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            fontSize: '1.1rem',
            fontWeight: 500,
            color: '#333333',
          },
        },
      },
    },
    // FormControl component styling
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 500,
            color: '#666666',
            '&.Mui-focused': {
              color: '#29204C',
            },
          },
        },
      },
    },
    // Avatar component styling
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#29204C',
          fontSize: '1.25rem',
          fontWeight: 500,
        },
      },
    },
  },
});

export default candidateTheme;